import { BaseUniDriver } from '@wix/wix-ui-test-utils/base-driver';
import { baseUniDriverFactory } from '../../utils/test-utils/unidriver';
import { UniDriver } from '@wix/unidriver-core';
import {
  ProgressBarDataHooks,
  ProgressBarDataKeys,
  ProgressBarAriaKeys,
  ProgressBarInnerAriaKeys,
} from './DataHooks';

const getNative = (base: UniDriver): Promise<HTMLElement> => {
  if (base.type !== 'react') {
    throw new Error('Supported only in React/DOM.');
  }
  return base.getNative();
};

export interface LinearProgressBarCoreUniDriver extends BaseUniDriver {
  /** Get the width of the foreground bar (the progress) */
  getWidth(): Promise<string>;
  /** Returns Promise<boolean> that indicates if the success icon exists */
  isSuccessIconDisplayed(): Promise<boolean>;
  /** Returns Promise<boolean> that indicates if the error icon exists */
  isErrorIconDisplayed(): Promise<boolean>;
  /** Returns Promise<boolean> that indicates if the progress percentages text exists */
  isPercentagesProgressDisplayed(): Promise<boolean>;
  /** Returns Promise<boolean> that indicates if the prefix indication exists */
  hasPrefixIndication(): Promise<boolean>;
  /** Returns Promise<boolean> that indicates if the custom suffix indication exists */
  hasSuffixIndication(): Promise<boolean>;
  /** Get the suffix indication's text */
  getSuffixIndicationText(): Promise<string>;
  /** Get the prefix indication aria-hidden value */
  hasPrefixAriaHidden(): Promise<boolean>;
  /** Get the progress indication aria-hidden value */
  hasProgressIndicatorAriaHidden(): Promise<boolean>;
  /** Get the progress percentages value */
  getValue(): Promise<string | null>;
  /** Get the progress numeric value */
  getNumericValue(): Promise<number>;
  /** Returns true if has progress completed (value is 100) */
  isCompleted(): Promise<boolean>;
  /** Returns true if has error */
  hasError(): Promise<boolean>;
  /** Returns min value prop */
  getMinValue(): Promise<number>;
  /** Returns max value prop */
  getMaxValue(): Promise<number>;
  /** Returns aria-valuenow prop */
  getAriaValueNow(): Promise<number>;
  /** Returns aria-valuemin prop */
  getAriaValueMin(): Promise<number>;
  /** Returns aria-valuemax prop */
  getAriaValueMax(): Promise<number>;
  /** Returns aria-label prop */
  getAriaLabel(): Promise<string>;
  /** Returns role html attribute */
  getRoleAttribute(): Promise<string>;
  /** Returns aria-valuetext prop */
  getAriaValueText(): Promise<string>;
}

export const linearProgressBarUniDriverFactory = (
  base: UniDriver,
): LinearProgressBarCoreUniDriver => {
  const byDataHook = (dataHook: string) => `[data-hook="${dataHook}"]`;

  const getValue = async () => {
    if (!(await base.exists())) {
      return null;
    }
    return base
      .$(byDataHook(ProgressBarDataHooks.progressPercentage))
      .$('span')
      .text();
  };

  const getDataAttribute = async (
    key: string,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type -- TODO(backlog 20260610T131344): type generically; cascades into nullable driver-interface returns
    parsingFunction?: Function,
  ) => {
    if (!(await base.exists()) || !(await base.attr(key))) {
      return null;
    }
    const value = await base.attr(key);
    return !!parsingFunction && parsingFunction instanceof Function
      ? parsingFunction(value)
      : value;
  };

  return {
    ...baseUniDriverFactory(base),
    getWidth: async () => {
      const bar = base.$(byDataHook(ProgressBarDataHooks.foreground));
      return (await getNative(bar)).style.width;
    },
    isSuccessIconDisplayed: () =>
      base.$(byDataHook(ProgressBarDataHooks.successIcon)).exists(),
    isErrorIconDisplayed: () =>
      base.$(byDataHook(ProgressBarDataHooks.errorIcon)).exists(),
    isPercentagesProgressDisplayed: () =>
      base.$(byDataHook(ProgressBarDataHooks.progressPercentage)).exists(),
    getValue: async () => getValue(),
    getNumericValue: () => getDataAttribute(ProgressBarDataKeys.value, Number),
    isCompleted: async () =>
      // @ts-ignore
      (await getValue()) >= (await getDataAttribute(ProgressBarDataKeys.max)),
    hasError: async () => (await base.attr('data-error')) === 'true',
    getMinValue: () => getDataAttribute(ProgressBarDataKeys.min, Number),
    getMaxValue: () => getDataAttribute(ProgressBarDataKeys.max, Number),
    getAriaValueNow: () =>
      getDataAttribute(ProgressBarAriaKeys.valuenow, Number),
    getAriaValueMax: () =>
      getDataAttribute(ProgressBarAriaKeys.valuemax, Number),
    getAriaValueMin: () =>
      getDataAttribute(ProgressBarAriaKeys.valuemin, Number),
    getAriaValueText: () => getDataAttribute(ProgressBarAriaKeys.valuetext),
    getRoleAttribute: () => getDataAttribute('role'),
    hasPrefixIndication: () =>
      base.$(byDataHook(ProgressBarDataHooks.prefixIndicator)).exists(),
    hasSuffixIndication: () =>
      base.$(byDataHook(ProgressBarDataHooks.suffixIndicator)).exists(),
    getSuffixIndicationText: () =>
      base.$(byDataHook(ProgressBarDataHooks.suffixIndicator)).text(),
    hasPrefixAriaHidden: async () =>
      (await base
        .$(byDataHook(ProgressBarDataHooks.prefixIndicator))
        .attr(ProgressBarInnerAriaKeys.hidden)) === 'true',
    hasProgressIndicatorAriaHidden: async () =>
      (await base
        .$(byDataHook(ProgressBarDataHooks.progressIndicator))
        .attr(ProgressBarInnerAriaKeys.hidden)) === 'true',
    getAriaLabel: () =>
      // @ts-ignore
      base
        .$(byDataHook(ProgressBarDataHooks.container))
        .attr(ProgressBarInnerAriaKeys.label),
  };
};
